import React, { useContext, useMemo } from 'react';
import {
  CustomField,
  CustomFieldType,
  CustomFieldValue,
  WithCustomFields,
} from '../../../types/custom_field/customField';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { Grid } from '@mui/material';
import CustomFieldInput from './CustomFieldInput';
import { classes, testIds } from '../../../util/identifiers/identifiers.util';
import { OrderInput, OrderUtil } from '../../../types/order/';
import { UserContext } from '../../../context/UserContext';
import { ContactContext } from '../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../context/ProductMasterDataContext';
import { Divider } from '../../components/Divider';
import { OrderFlowContext } from '../../../context/OrderFlowContext';
import { GXO } from '../../../types/customer/GXO/GXO';

interface CustomFieldOrderInputProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  disabled?: boolean;
}

export default function CustomFieldOrderInput({ order, setOrder, disabled = false }: CustomFieldOrderInputProps) {
  const { customFields } = useContext(CustomFieldContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { orderFlows } = useContext(OrderFlowContext);

  const entitySubtype = OrderUtil.typeToEntitySubType(order.type);

  const flow = orderFlows.get(order.flowId);
  if (!flow) return null;

  if (order.companyId === GXO.companyId && !order.id) {
    return null;
  }

  const items = useMemo(() => {
    return [...customFields.values()]
      .filter(v => flow.customFieldIds.includes(v.id))
      .sort((a, b) => b.index - a.index || a.name.localeCompare(b.name));
  }, [customFields, order]);

  const customFieldText = (field: CustomField, value?: string) => {
    if (!value) return '';

    if (field.type === CustomFieldType.date) {
      return value;
    }

    return value || '';
  };

  const content = (item: CustomField, cf?: CustomFieldValue) => {
    if (item.generated && cf?.value === undefined) return null;

    return (
      <Grid
        key={cf?.id || item.id}
        item
        xs={cf?.generated ? 3 : item.type === CustomFieldType.bool || item.type === CustomFieldType.image ? 12 : 6}
        mt={item.type === CustomFieldType.bool ? 1 : 0}
        className={classes.customFieldInput.name}
        data-testid={testIds.customFieldInput(item.name)}
      >
        <CustomFieldInput
          disabled={
            (disabled && item.onCreate) ||
            !!(order.id && item.mandatory) ||
            (item.generated && item.onCreate) ||
            cf?.generated
          }
          value={customFieldText(item, cf?.value)}
          entity={order}
          item={item}
          onChange={setOrder}
          name={cf?.name}
          generated={cf?.generated}
          index={cf?.index}
          id={cf?.id}
          users={companyUsers}
          contacts={contacts}
          productMasterData={productMasterData}
        />
      </Grid>
    );
  };

  const customValues = order.customFieldValues();
  const toFilter = new Set(
    order
      .customFieldValues()
      .filter(item => item.id.includes('::'))
      .map(item => item.id.split('::')[0]),
  );

  const children = useMemo(() => {
    return [
      ...items.map(item => {
        if (toFilter.has(item.id)) return null;
        if (!item.onCreate && !order.id) return null;

        const cf = WithCustomFields.toCustomFieldsMap(order).get(item.id);
        return content(item, cf);
      }),

      ...customValues.map(cf => {
        if (!cf.id.includes('::')) return null;

        const [id, suffix] = cf.id.split('::');
        if (!id) return null;

        const item = customFields.get(id);
        if (!item) return null;

        return content(item, cf);
      }),
    ];
  }, [items, customValues, customFields, order]);

  if (!children.length) return null;

  return (
    <>
      <Divider className='my-4' />
      <Grid container rowSpacing={1} columnSpacing={1}>
        {children}
      </Grid>
    </>
  );
}
